import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useOrganizationStore } from '@/stores/organization-store'
import { notionIntegrationService } from '@/services/notion-integration'
import type { NotionConnection } from '@/schemas/notion-integration'
import { Cloud, CloudOff, Loader2, ExternalLink } from 'lucide-react'

interface NotionConnectionProps {
  /**
   * Callback chamado quando conexao e estabelecida ou desconectada
   */
  onConnectionChange?: (connected: boolean, connection: NotionConnection | null) => void
}

export function NotionConnectionCard({
  onConnectionChange,
}: NotionConnectionProps) {
  const { t } = useTranslation()
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization)
  const organizationId = currentOrganization?.id

  const [isLoading, setIsLoading] = useState(true)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [connection, setConnection] = useState<NotionConnection | null>(null)
  const [pendingState, setPendingState] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch connection status on mount
  const fetchStatus = useCallback(async () => {
    if (!organizationId) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const status = await notionIntegrationService.getStatus(organizationId)
      setConnected(status.connected)
      setConnection(status.connection)
      onConnectionChange?.(status.connected, status.connection)
    } catch (err) {
      console.error('Failed to fetch Notion connection status:', err)
      setError(t('settings.integrations.errors.checkConnection'))
    } finally {
      setIsLoading(false)
    }
  }, [organizationId, onConnectionChange, t])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  // Handle OAuth callback via postMessage
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return

      const data = event.data
      if (data?.type !== 'notion-oauth-callback') return

      const { code, state, error: oauthError } = data

      if (oauthError) {
        console.error('OAuth error:', oauthError)
        setError(t('settings.integrations.errors.notionAuth'))
        setIsConnecting(false)
        setPendingState(null)
        return
      }

      // Verify state matches
      if (state !== pendingState) {
        setError(t('settings.integrations.errors.invalidState'))
        setIsConnecting(false)
        setPendingState(null)
        return
      }

      try {
        const result = await notionIntegrationService.handleCallback(organizationId!, {
          code,
          state,
        })

        setConnected(true)
        setConnection(result.connection)
        setError(null)
        onConnectionChange?.(true, result.connection)
      } catch (err) {
        console.error('OAuth callback failed:', err)
        setError(t('settings.integrations.errors.connectFailed'))
      } finally {
        setIsConnecting(false)
        setPendingState(null)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [organizationId, pendingState, onConnectionChange, t])

  const handleConnect = async () => {
    if (!organizationId) {
      setError(t('settings.integrations.errors.selectOrg'))
      return
    }

    try {
      setIsConnecting(true)
      setError(null)

      const { auth_url, state } = await notionIntegrationService.initiateAuth(
        organizationId
      )

      setPendingState(state)

      // Open OAuth popup
      const width = 500
      const height = 700
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        auth_url,
        'notion-oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      if (!popup) {
        setError(t('settings.integrations.errors.popupBlocked'))
        setIsConnecting(false)
        setPendingState(null)
        return
      }

      // Check if popup was closed without completing auth
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed)
          // Only reset if we're still waiting for this specific state
          setPendingState((currentState) => {
            if (currentState === state) {
              setIsConnecting(false)
              return null
            }
            return currentState
          })
        }
      }, 500)
    } catch (err) {
      console.error('Failed to initiate OAuth:', err)
      setError(t('settings.integrations.errors.initAuthFailed'))
      setIsConnecting(false)
      setPendingState(null)
    }
  }

  const handleDisconnect = async () => {
    if (!organizationId) return

    try {
      setIsDisconnecting(true)
      setError(null)
      await notionIntegrationService.disconnect(organizationId)
      setConnected(false)
      setConnection(null)
      onConnectionChange?.(false, null)
    } catch (err) {
      console.error('Failed to disconnect:', err)
      setError(t('settings.integrations.errors.disconnectFailed'))
    } finally {
      setIsDisconnecting(false)
    }
  }

  // No organization selected
  if (!organizationId) {
    return null
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t('settings.integrations.checking')}</span>
      </div>
    )
  }

  // Connected state
  if (connected && connection) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <Cloud className="h-4 w-4 text-emerald-500" />
          <span className="text-sm text-emerald-400">{connection.workspace_name}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDisconnect}
          disabled={isDisconnecting}
          className="text-gray-400 hover:text-red-400"
        >
          {isDisconnecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CloudOff className="h-4 w-4" />
          )}
          <span className="ml-1.5">{t('settings.integrations.disconnect')}</span>
        </Button>
      </div>
    )
  }

  // Disconnected state
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleConnect}
        disabled={isConnecting}
        className="border-gray-700 hover:border-emerald-500/50 hover:bg-emerald-500/10"
      >
        {isConnecting ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <ExternalLink className="h-4 w-4 mr-2" />
        )}
        {isConnecting ? t('settings.integrations.connecting') : t('settings.integrations.connectNotion')}
      </Button>
      {error && (
        <span className="text-xs text-red-400">{error}</span>
      )}
    </div>
  )
}
